/**
 * CORE-BN — Front-of-House Attention Capacity · the model
 *
 * Constitutional authority: CORE-BN. This module is the capability —
 * everything else in this folder feeds it or renders it.
 *
 * What it does
 * ------------
 *   AVAILABLE  = headcount x windowMinutes x personEffectiveAttentionFraction
 *   CONSUMED   = sum over claims of (count x unitWeight x dwellEscalation)
 *   UTILIZATION= CONSUMED / AVAILABLE
 *   STATE      = the band UTILIZATION falls into
 *
 * Both sides are in the same unit: ATTENTION-MINUTES within the window.
 * That unit is chosen because it is the only one in which the numerator
 * and the denominator are commensurable, and because it produces a
 * headroom figure a human can act on ("you are eleven minutes of
 * attention short of this half hour").
 *
 * What it deliberately does NOT do
 * --------------------------------
 *   - It does not divide anything by a person. There is no per-advisor
 *     load, because a per-advisor load is a performance measure wearing a
 *     capacity model's clothes (Responsibility 7).
 *   - It does not read a clock. The caller injects the window, which is
 *     what makes identical inputs produce identical outputs.
 *   - It does not invent capacity. With no staffing basis it returns
 *     `'unknown'` and the shop plans exactly as it does today
 *     (Responsibility 8).
 *   - It does not treat "cannot observe" as "zero". An unobserved or
 *     unreadable claim contributes nothing to CONSUMED and lowers
 *     confidence, because a model that silently reads missing demand as
 *     absent demand reports comfort at precisely the wrong moment.
 *
 * Purity: no I/O, no clock, no React, no Supabase. Pure function of its
 * arguments.
 */

import {
  FOH_ATTENTION_CLAIM_KINDS,
  FOH_ATTENTION_CLAIM_LABELS,
  FOH_DWELL_BUCKETS,
  isContributingBasis,
  isCustodyClaimKind,
  type FohAttentionBasis,
  type FohAttentionCapacityResult,
  type FohAttentionClaimContribution,
  type FohAttentionClaimKind,
  type FohAttentionObservations,
  type FohClaimObservation,
  type FohDwellBucket,
  type FohDwellDistribution,
  type FohEstimateConfidence,
  type FohInsufficientBasisReason,
  type FohSaturationBand,
  type FohSaturationState,
} from './types';
import {
  defaultFohAttentionPolicy,
  toAppliedPolicySnapshot,
  type FohAttentionPolicy,
} from './policy';

// =============================================================================
// Numeric hygiene
// =============================================================================

/** Non-negative finite count. Garbage (NaN, Infinity, negatives, non-numbers) becomes 0. */
function safeCount(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
  return v;
}

/**
 * Round to 2dp. Attention-minutes are an estimate; carrying float noise
 * into a human-facing number implies a precision the model does not have.
 */
function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

// =============================================================================
// Window
// =============================================================================

export interface FohWindow {
  startIso: string;
  endIso: string;
  minutes: number;
  valid: boolean;
}

/**
 * Derive the window's length from its bounds. An unparseable or
 * non-positive window is invalid, which forces `'unknown'` rather than a
 * divide-by-zero dressed up as a capacity figure.
 */
export function resolveFohWindow(startIso: string, endIso: string): FohWindow {
  const startMs = Date.parse(startIso);
  const endMs = Date.parse(endIso);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return { startIso, endIso, minutes: 0, valid: false };
  }
  const minutes = (endMs - startMs) / 60_000;
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return { startIso, endIso, minutes: 0, valid: false };
  }
  return { startIso, endIso, minutes, valid: true };
}

// =============================================================================
// Available attention
// =============================================================================

/**
 * Attention-minutes the front of house actually has in the window.
 *
 * Capped at `windowMinutes` per person by construction, because
 * `personEffectiveAttentionFraction` is bounded at 1 in policy. Nobody
 * supplies more than the window's worth of themselves.
 */
export function computeAvailableAttentionMinutes(
  headcount: number,
  windowMinutes: number,
  personEffectiveAttentionFraction: number,
): number {
  const people = Math.max(0, Math.floor(safeCount(headcount)));
  const minutes = safeCount(windowMinutes);
  const fraction = Math.min(1, Math.max(0, safeCount(personEffectiveAttentionFraction)));
  return round2(people * minutes * fraction);
}

// =============================================================================
// Claim weighting
// =============================================================================

const ZERO_DWELL: FohDwellDistribution = Object.freeze({
  normal: 0,
  warning: 0,
  urgent: 0,
});

/**
 * Normalise a caller-supplied dwell distribution against the claim's
 * count.
 *
 * Defensive on purpose: the distribution comes from an aggregation the
 * model does not control. Rules:
 *   - Absent distribution -> the whole count sits in `'normal'`. A claim
 *     with no dwell evidence is charged at parity, never escalated.
 *   - Buckets summing to less than `count` -> the shortfall lands in
 *     `'normal'` (conservative: unexplained items are not escalated).
 *   - Buckets summing to more than `count` -> scaled down proportionally,
 *     so a bad aggregation cannot inflate consumption past the count it
 *     claims to describe.
 */
export function normalizeDwellDistribution(
  count: number,
  dwell: FohDwellDistribution | undefined,
): FohDwellDistribution {
  const total = safeCount(count);
  if (total === 0) return ZERO_DWELL;
  if (!dwell) return Object.freeze({ normal: total, warning: 0, urgent: 0 });

  const raw: Record<FohDwellBucket, number> = {
    normal: safeCount(dwell.normal),
    warning: safeCount(dwell.warning),
    urgent: safeCount(dwell.urgent),
  };
  const sum = raw.normal + raw.warning + raw.urgent;

  if (sum === 0) return Object.freeze({ normal: total, warning: 0, urgent: 0 });

  if (sum > total) {
    const scale = total / sum;
    return Object.freeze({
      normal: raw.normal * scale,
      warning: raw.warning * scale,
      urgent: raw.urgent * scale,
    });
  }

  return Object.freeze({
    normal: raw.normal + (total - sum),
    warning: raw.warning,
    urgent: raw.urgent,
  });
}

/**
 * Attention-minutes contributed by one observed claim, plus the
 * explanation of how that number was reached.
 *
 * `waiter_present` is the one claim that does not multiply a
 * per-occurrence weight, because lobby presence is continuous rather
 * than discrete. Its cost per waiter is a fraction of the WINDOW, so its
 * reported `unitWeightMinutes` is that derived per-waiter minute figure —
 * which keeps the contribution shape uniform and the arithmetic
 * inspectable (`count x unitWeightMinutes = attentionMinutes`).
 */
export function computeClaimContribution(
  observation: FohClaimObservation,
  policy: FohAttentionPolicy,
  windowMinutes: number,
): FohAttentionClaimContribution {
  const kind = observation.kind;
  const label = FOH_ATTENTION_CLAIM_LABELS[kind] ?? kind;
  const count = safeCount(observation.count);
  const basis = observation.basis;
  const contributed = isContributingBasis(basis);

  // A claim the shop could not observe contributes nothing. It is still
  // reported, so the reader can see what the estimate does NOT include.
  if (!contributed) {
    return {
      kind,
      label,
      count: 0,
      basis,
      unitWeightMinutes: policy.claimWeightMinutes[kind] ?? 0,
      weightSource: policy.claimWeightSources[kind] ?? 'default',
      dwellEscalation: 1,
      attentionMinutes: 0,
      contributed: false,
    };
  }

  if (kind === 'waiter_present') {
    const perWaiterMinutes = round2(
      windowMinutes * policy.waiterPresenceWindowFraction,
    );
    return {
      kind,
      label,
      count,
      basis,
      unitWeightMinutes: perWaiterMinutes,
      weightSource: policy.waiterPresenceWindowFractionSource,
      ...(observation.presence ? { presence: observation.presence } : {}),
      dwellEscalation: 1,
      attentionMinutes: round2(count * perWaiterMinutes),
      contributed: true,
    };
  }

  const unitWeightMinutes = policy.claimWeightMinutes[kind] ?? 0;
  const weightSource = policy.claimWeightSources[kind] ?? 'default';

  if (!isCustodyClaimKind(kind)) {
    return {
      kind,
      label,
      count,
      basis,
      unitWeightMinutes,
      weightSource,
      ...(observation.presence ? { presence: observation.presence } : {}),
      dwellEscalation: 1,
      attentionMinutes: round2(count * unitWeightMinutes),
      contributed: true,
    };
  }

  // Custody claims escalate with dwell (CORE-BN-GAP-003).
  const dwell = normalizeDwellDistribution(count, observation.dwell);
  const mult = policy.dwellEscalationMultipliers;
  const weightedUnits =
    dwell.normal * mult.normal +
    dwell.warning * mult.warning +
    dwell.urgent * mult.urgent;
  const attentionMinutes = round2(weightedUnits * unitWeightMinutes);
  const baseline = count * unitWeightMinutes;
  const dwellEscalation =
    baseline > 0 ? round2(attentionMinutes / baseline) : 1;

  return {
    kind,
    label,
    count,
    basis,
    unitWeightMinutes,
    weightSource,
    dwell,
    dwellEscalation,
    attentionMinutes,
    contributed: true,
  };
}

// =============================================================================
// Banding
// =============================================================================

/**
 * Map a utilization to its band.
 *
 * Boundaries are lower-inclusive: a utilization exactly equal to a
 * threshold belongs to the WORSE band. A shop sitting exactly on its
 * saturation line is saturated, not loaded — rounding a boundary toward
 * comfort is how a capacity model becomes decorative.
 */
export function bandForUtilization(
  utilization: number,
  thresholds: Readonly<{ loaded: number; saturated: number; overCapacity: number }>,
): FohSaturationBand {
  if (utilization >= thresholds.overCapacity) return 'over_capacity';
  if (utilization >= thresholds.saturated) return 'saturated';
  if (utilization >= thresholds.loaded) return 'loaded';
  return 'comfortable';
}

// =============================================================================
// Confidence
// =============================================================================

/**
 * Confidence in the estimate, derived deterministically from how much of
 * the claim vocabulary was actually observed and how the denominator was
 * established.
 *
 * The ceiling rules matter more than the arithmetic:
 *   - A roster-based denominator can never be `'high'`. The roster says
 *     who works here, not who is on the floor.
 *   - Any unreadable source caps confidence at `'low'`. A failed query is
 *     not a small blemish on an estimate; it is a hole in it.
 */
export function deriveConfidence(args: {
  staffingBasis: FohAttentionBasis['staffingBasis'];
  observedClaimCoverage: number;
  hasUnavailableSources: boolean;
  hasExtrapolatedClaims: boolean;
  hasInferredClaims?: boolean;
}): FohEstimateConfidence {
  if (args.staffingBasis === 'none') return 'none';
  if (args.observedClaimCoverage <= 0) return 'none';
  if (args.hasUnavailableSources) return 'low';

  let level: FohEstimateConfidence =
    args.observedClaimCoverage >= 0.75
      ? 'high'
      : args.observedClaimCoverage >= 0.5
        ? 'moderate'
        : 'low';

  if (args.staffingBasis === 'roster' && level === 'high') level = 'moderate';
  if (args.hasExtrapolatedClaims && level === 'high') level = 'moderate';
  // A claim standing on an appointment rather than on a vehicle is a
  // prediction. An estimate resting on predictions is not a high-
  // confidence reading of the present.
  if (args.hasInferredClaims && level === 'high') level = 'moderate';
  return level;
}

// =============================================================================
// The estimate
// =============================================================================

export interface EstimateFohAttentionCapacityArgs {
  observations: FohAttentionObservations;
  /** Resolved CORE-BI policy. Defaults to the untuned conservative model. */
  policy?: FohAttentionPolicy;
  /**
   * The shop this estimate is for. When supplied and different from
   * `observations.shopId`, the model refuses to answer (SP-04) rather
   * than reporting one shop's saturation under another shop's id.
   */
  expectedShopId?: string;
}

/**
 * Estimate front-of-house attention capacity for one shop, for one
 * window.
 *
 * Returns `'unknown'` — never a fabricated band — when:
 *   - the window is unparseable or non-positive;
 *   - the observations belong to a different shop than requested;
 *   - there is no front-of-house staffing basis (nobody on, or no way to
 *     tell who is on);
 *   - not one claim in the vocabulary could be observed.
 *
 * Note the asymmetry that carries the doctrine: observing every claim and
 * finding NOTHING is `'comfortable'` with zero consumption. Observing
 * nothing at all is `'unknown'`. Collapsing those two would let a broken
 * pipeline report a calm shop.
 */
export function estimateFohAttentionCapacity(
  args: EstimateFohAttentionCapacityArgs,
): FohAttentionCapacityResult {
  const { observations } = args;
  const policy = args.policy ?? defaultFohAttentionPolicy();
  const window = resolveFohWindow(
    observations.windowStartIso,
    observations.windowEndIso,
  );

  const reasons: FohInsufficientBasisReason[] = [];

  if (
    typeof args.expectedShopId === 'string' &&
    args.expectedShopId !== observations.shopId
  ) {
    reasons.push('shop_mismatch');
  }
  if (!window.valid) reasons.push('invalid_window');

  // Staffing. A shop-stated headcount overrides observation, because a
  // human declaring their front-of-house size outranks an inference
  // about it.
  const observedHeadcount = Math.max(
    0,
    Math.floor(safeCount(observations.staffing?.headcount)),
  );
  const overrideHeadcount = policy.staffHeadcountOverride;
  const headcount =
    overrideHeadcount !== null ? overrideHeadcount : observedHeadcount;
  const observedBasis = observations.staffing?.basis ?? 'none';
  const timeClockStatus = observations.staffing?.timeClockStatus ?? 'unavailable';

  // A zero headcount from a source that KNOWS it is zero keeps its
  // basis. Only a source that could not tell us anything reports
  // `'none'`. Collapsing the two would turn "nobody is on the desk" into
  // "we have no idea", which is a strictly less useful thing to tell a
  // manager at 7:30 in the morning.
  const knowsItsZero =
    overrideHeadcount !== null ||
    (observedBasis === 'clocked_in' && timeClockStatus === 'available');
  const staffingBasis =
    overrideHeadcount !== null
      ? 'shop_override'
      : headcount > 0 || knowsItsZero
        ? observedBasis
        : 'none';

  if (headcount <= 0) {
    reasons.push(knowsItsZero ? 'no_foh_staff_on_duty' : 'no_foh_staffing');
  } else if (staffingBasis === 'none') {
    reasons.push('no_foh_staffing');
  }

  // Claims. One contribution per vocabulary entry, in vocabulary order,
  // so the explanation is total: a reader sees every claim the model
  // knows about, including the ones it could not see.
  const byKind = new Map<FohAttentionClaimKind, FohClaimObservation>();
  for (const obs of observations.claims ?? []) {
    if (!obs || !FOH_ATTENTION_CLAIM_KINDS.includes(obs.kind)) continue;
    // Last write wins; duplicates are a caller bug, not a reason to throw.
    byKind.set(obs.kind, obs);
  }

  const contributions: FohAttentionClaimContribution[] = [];
  for (const kind of FOH_ATTENTION_CLAIM_KINDS) {
    const obs = byKind.get(kind) ?? {
      kind,
      count: 0,
      basis: 'unobserved' as const,
    };
    contributions.push(
      computeClaimContribution(obs, policy, window.valid ? window.minutes : 0),
    );
  }

  const unobservedClaimKinds = contributions
    .filter((c) => c.basis === 'unobserved')
    .map((c) => c.kind);
  const unavailableClaimKinds = contributions
    .filter((c) => c.basis === 'unavailable')
    .map((c) => c.kind);
  const extrapolatedClaimKinds = contributions
    .filter((c) => c.basis === 'trailing_rate')
    .map((c) => c.kind);
  const inferredClaimKinds = contributions
    .filter((c) => c.basis === 'inferred')
    .map((c) => c.kind);
  const contributingCount = contributions.filter((c) => c.contributed).length;
  const observedClaimCoverage =
    FOH_ATTENTION_CLAIM_KINDS.length > 0
      ? contributingCount / FOH_ATTENTION_CLAIM_KINDS.length
      : 0;

  if (contributingCount === 0) reasons.push('no_observed_claims');

  const unavailableSources = Object.freeze([
    ...(observations.unavailableSources ?? []),
  ]);

  const estimatedConsumedAttentionMinutes = round2(
    contributions.reduce((sum, c) => sum + c.attentionMinutes, 0),
  );
  const availableAttentionMinutes = window.valid
    ? computeAvailableAttentionMinutes(
        headcount,
        window.minutes,
        policy.personEffectiveAttentionFraction,
      )
    : 0;

  const basis: FohAttentionBasis = Object.freeze({
    staffingBasis,
    timeClockStatus,
    fohHeadcount: headcount,
    confidence: deriveConfidence({
      staffingBasis,
      observedClaimCoverage,
      hasUnavailableSources:
        unavailableSources.length > 0 || unavailableClaimKinds.length > 0,
      hasExtrapolatedClaims: extrapolatedClaimKinds.length > 0,
      hasInferredClaims: inferredClaimKinds.length > 0,
    }),
    unobservedClaimKinds: Object.freeze(unobservedClaimKinds),
    unavailableClaimKinds: Object.freeze(unavailableClaimKinds),
    unavailableSources,
    extrapolatedClaimKinds: Object.freeze(extrapolatedClaimKinds),
    inferredClaimKinds: Object.freeze(inferredClaimKinds),
    observedClaimCoverage: round2(observedClaimCoverage),
  });

  // Explanation order: loudest contributor first. A reader scanning the
  // list should meet the reason before the footnotes.
  const orderedClaims = Object.freeze(
    [...contributions].sort((a, b) => {
      if (b.attentionMinutes !== a.attentionMinutes) {
        return b.attentionMinutes - a.attentionMinutes;
      }
      return (
        FOH_ATTENTION_CLAIM_KINDS.indexOf(a.kind) -
        FOH_ATTENTION_CLAIM_KINDS.indexOf(b.kind)
      );
    }),
  );

  const appliedPolicy = toAppliedPolicySnapshot(
    policy,
    window.valid ? window.minutes : 0,
  );

  const base = {
    shopId: observations.shopId,
    windowStartIso: observations.windowStartIso,
    windowEndIso: observations.windowEndIso,
    windowMinutes: window.valid ? round2(window.minutes) : 0,
    isEstimate: true as const,
    evidenceClass: 'derivation' as const,
    availableAttentionMinutes,
    estimatedConsumedAttentionMinutes,
    contributingClaims: orderedClaims,
    appliedPolicy,
    basis,
  };

  if (reasons.length > 0 || availableAttentionMinutes <= 0) {
    if (
      availableAttentionMinutes <= 0 &&
      !reasons.includes('no_foh_staffing') &&
      !reasons.includes('no_foh_staff_on_duty') &&
      !reasons.includes('invalid_window')
    ) {
      // Defence in depth: a zero denominator with no recorded reason
      // would otherwise divide by zero. Name it rather than hide it.
      reasons.push('no_foh_staffing');
    }
    return {
      ...base,
      state: 'unknown' as FohSaturationState,
      band: null,
      utilization: null,
      headroomAttentionMinutes: null,
      insufficientBasisReasons: Object.freeze(reasons),
    };
  }

  const utilization =
    estimatedConsumedAttentionMinutes / availableAttentionMinutes;
  const band = bandForUtilization(utilization, policy.saturationThresholds);

  return {
    ...base,
    state: band,
    band,
    utilization: round2(utilization),
    headroomAttentionMinutes: round2(
      Math.max(0, availableAttentionMinutes - estimatedConsumedAttentionMinutes),
    ),
    insufficientBasisReasons: Object.freeze([]),
  };
}

/**
 * Explanation sentence for a result, in the constitution's own shape:
 * "Front of house is saturated because X intake claims, Y waiting
 * customers, Z aged custody items."
 *
 * Shop-scoped by construction — the subject of every sentence this
 * produces is "front of house", never a person.
 */
export function explainFohAttentionResult(
  result: FohAttentionCapacityResult,
  maxClaims = 3,
): string {
  if (result.state === 'unknown') {
    return 'Front-of-house attention cannot be estimated right now, so the shop plans as it normally would.';
  }
  const top = result.contributingClaims
    .filter((c) => c.contributed && c.attentionMinutes > 0)
    .slice(0, Math.max(1, maxClaims));
  if (top.length === 0) {
    return `Front of house is ${labelFor(result.state)} — no front-of-house claims are open in this window.`;
  }
  const parts = top.map((c) => `${formatCount(c.count)} ${lowerFirst(c.label)}`);
  return `Front of house is ${labelFor(result.state)} because of ${joinList(parts)}.`;
}

function labelFor(state: FohSaturationState): string {
  switch (state) {
    case 'comfortable':
      return 'comfortable';
    case 'loaded':
      return 'loaded';
    case 'saturated':
      return 'saturated';
    case 'over_capacity':
      return 'over capacity';
    default:
      return 'unknown';
  }
}

function formatCount(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function lowerFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

function joinList(parts: string[]): string {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Re-exported for consumers that only need the bucket vocabulary. */
export { FOH_DWELL_BUCKETS };
